import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { json, redirect } from "@remix-run/cloudflare";
import { useFetcher, useLoaderData, useParams, useNavigate } from "@remix-run/react";
import CheckOutImage from "../assets/checkout/checkout.webp";
import CheckOutImage2 from "../assets/checkout/checkout2.webp";
import { motion, AnimatePresence } from "framer-motion";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";

import { AddressElement, Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import Testimonial from "../components/sales/testimonial/Testimonial";
import CountdownTimer from "../components/utils/CountdownTimer";
import ToastNotification from "../components/utils/ToastNotification";

import { useDebounceValue } from "usehooks-ts";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import UnderLine from "../assets/hero/Vector.png";
import StrapiLogoPill from "../assets/checkout/Strapi.full.logo.dark.svg";
import SandClock from "../assets/checkout/sand-clock.png";
import CheckoutLiveVisitors from "../components/checkout/live-visitors";
import WebpackLogo from "../assets/checkout/webpack.png";
import StrapiLogo from "../assets/logo/strapi.png";

import { ClientOnly } from "remix-utils/client-only";

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(
  "pk_live_51Q0luCLV0WATdPxEnqQzJja9o4MEo2XOY8O6H4OvJOAL9ZfgaMo5M6HPREUb7DK8fNStO3OOSPu2yhyY1mouTkn0000M2d03ci"
);

export async function loader({ request, params, context }) {
  const { getSession, commitSession } = context.sessionStorage;
  const session = await getSession(request.headers.get("Cookie"));

  const { priceId } = params;

  // Dynamically import Stripe only when the action is executed server-side
  const Stripe = await import("stripe");
  const stripe = new Stripe.default(context.cloudflare.env.STRIPE_SECRET_KEY);

  // Get the price from the Stripe API
  const price = await stripe.prices.retrieve(priceId);

  // Get the product details
  const product = await stripe.products.retrieve(price.product);

  const headers = new Headers();
  const checkoutSessions = session.get("checkout") || {}; // Store multiple checkouts per priceId

  const getClientIP = (request) => {
    const ip =
      request.headers.get("CF-Connecting-IP") ||
      request.headers.get("X-Forwarded-For") ||
      request.headers.get("X-Real-Ip") ||
      request.socket?.remoteAddress ||
      "127.0.0.1";

    console.log("IP", ip);
    return ip;
  };

  console.log("Checkout session", checkoutSessions);

  if (!checkoutSessions[priceId]) {
    const ecommerceData = {
      debug_mode: true,
      engagement_time_msec: 1023,
      currency: "EUR",
      value: price.unit_amount / 100,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          item_list_name: "Checkout",
          item_list_id: "checkout",
          price: price.unit_amount / 100,
          quantity: 1,
          item_category: "Plugins",
          item_brand: "Headlockr",
        },
      ],
    };

    checkoutSessions[priceId] = ecommerceData;
    session.set("checkout", checkoutSessions);

    headers.append(
      "Set-Cookie",
      await commitSession(session, { maxAge: 60 * 60 * 24 * 7 }) // 1 week
    );

    try {
      await axios.post(
        `${context.cloudflare.env.STRAPI_BASE_URL}/api/measurement/collect`,
        {
          events: [
            {
              name: "begin_checkout",
              params: ecommerceData,
            },
            {
              name: "view_item",
              params: ecommerceData,
            },
          ],
        },
        {
          headers: {
            Authorization: `Bearer ${context.cloudflare.env.STRAPI_API_TOKEN}`,
            Cookie: request.headers.get("Cookie") || "",
          },
        }
      );
    } catch (error) {
      console.error("Error sending GA4 event:", error);
    }
  }

  try {
    const metaPixelData = {
      event_time: Math.floor(Date.now() / 1000),
      action_source: "website",
      event_source_url: request.url,
      currency: "EUR",
      value: price.unit_amount / 100,
      content_name: product.name,
      content_ids: [product.id],
      content_type: "Plugins",
      num_items: 1,
      contents: [
        {
          id: product.id,
          quantity: 1,
          item_price: price.unit_amount / 100,
        },
      ],
    };

    await axios.post(
      `${context.cloudflare.env.STRAPI_BASE_URL}/api/pixel/collect`,
      {
        events: [
          {
            event_name: "InitiateCheckout",
            user_data: {
              client_ip_address: getClientIP(request),
            },
            ...metaPixelData,
          },
          {
            event_name: "ViewContent",
            user_data: {
              client_ip_address: getClientIP(request),
            },
            ...metaPixelData,
          },
        ],
      },
      {
        headers: {
          Authorization: `Bearer ${context.cloudflare.env.STRAPI_API_TOKEN}`,
          Cookie: request.headers.get("Cookie") || "",
        },
      }
    );
  } catch (error) {
    console.error("Error sending Facebook Pixel event:", error);
  }

  return json(
    {
      amount: price.unit_amount,
      subtotal: price.unit_amount / 100,
      name: product.name,
      productImage: product.images,
      price,
      metaData: product.metadata,
      createdAt: new Date(product.created * 1000).toISOString(),
      updatedAt: new Date(product.updated * 1000).toISOString(),
      ENV: {
        PUBLIC_URL: "https://headlockr.io",
      },
    },
    { headers }
  );
}

export const meta = ({ data }) => {
  const { name, price, productImage, subtotal, metaData, createdAt, updatedAt } = data;
  const taxIncluded = price.tax_behavior === "inclusive" ? true : false;
  const currency = price.currency.toUpperCase();

  return [
    { title: "Checkout - HeadLockr Secure MFA Plugin for Strapi" },
    {
      property: "og:title",
      content: `Checkout - ${name} plan for Headlockr`,
    },
    {
      property: "og:description",
      content: `Headlockr's ${name} plan protects up to ${metaData.project_limit} projects with industry-leading MFA. Secure your Strapi admin panel with SMS, TOTP, and backup codes.`,
    },
    {
      property: "og:url",
      content: `https://headlockr.io/checkout/${price.id}`,
    },
    {
      property: "og:image",
      content: productImage?.[0] || "https://www.headlockr.com/checkout-og-image.jpg",
    },
    {
      name: "description",
      content: `Headlockr's ${name} plan protects up to ${metaData.project_limit} projects with industry-leading MFA. Secure your Strapi admin panel with SMS, TOTP, and backup codes.`,
    },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: `Checkout - ${name} plan for Headlockr`,
    },
    {
      name: "twitter:description",

      content: `Headlockr's ${name} plan protects up to ${metaData.project_limit} projects with industry-leading MFA. Secure your Strapi admin panel with SMS, TOTP, and backup codes.`,
    },
    {
      name: "twitter:image",
      content: productImage?.[0] || "https://www.headlockr.com/checkout-og-image.jpg",
    },
    { name: "twitter:site", content: "@headlockr" },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": ["WebPage", "CheckoutPage"],
            "@id": `https://headlockr.io/checkout/${price.id}`,
            url: `https://headlockr.io/checkout/${price.id}`,
            name: "Checkout &#8226; HeadLockr Secure MFA Plugin",
            isPartOf: { "@id": "https://headlockr.io/#website" },
            about: { "@id": "https://headlockr.io/#organization" },
            primaryImageOfPage: {
              "@id": "https://headlockr.io/#primaryimage",
            },
            image: { "@id": "https://headlockr.io/#primaryimage" },
            thumbnailUrl: productImage?.[0] || "https://www.headlockr.com/checkout-og-image.jpg",
            datePublished: createdAt,
            dateModified: updatedAt,
            description: `Headlockr's ${name} plan protects up to ${metaData.project_limit} projects with industry-leading MFA. Secure your Strapi admin panel with SMS, TOTP, and backup codes.`,
            inLanguage: "en-US",
            potentialAction: [
              {
                "@type": "OrderAction",
                target: {
                  "@type": "EntryPoint",
                  urlTemplate: `https://headlockr.io/checkout/${price.id}`,
                },
                deliveryMethod: "https://schema.org/DownloadAction",
                price: subtotal,
                priceCurrency: currency,
                priceSpecification: {
                  "@type": "PriceSpecification",
                  price: subtotal,
                  priceCurrency: currency,
                  valueAddedTaxIncluded: taxIncluded,
                },
                instrument: {
                  "@type": "PaymentMethod",
                  name: "Credit Card",
                },
                object: {
                  "@type": "Product",
                  "@id": "https://headlockr.io/#software",
                  name: `HeadLockr ${name} Plan`,
                  description: `Headlockr's ${name} plan protects up to ${metaData.project_limit} projects with industry-leading MFA. Secure your Strapi admin panel with SMS, TOTP, and backup codes.`,
                  sku: price.id,
                  productID: price.product,
                  image: productImage?.[0] || "https://www.headlockr.com/logo.png",
                  offers: {
                    "@type": "Offer",
                    availability: "https://schema.org/InStock",
                    url: `https://headlockr.io/checkout/${price.id}`,
                    validFrom: createdAt, // Ensure the timestamp is in milliseconds
                    priceSpecification: {
                      "@type": "PriceSpecification",
                      priceCurrency: currency,
                      price: subtotal,
                      valueAddedTaxIncluded: taxIncluded,
                      hasMerchantReturnPolicy: {
                        "@type": "MerchantReturnPolicy",
                        applicableCountry: "NL",
                        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
                        merchantReturnDays: 7,
                        returnMethod: "https://schema.org/ReturnByMail",
                        returnFees: "https://schema.org/FreeReturn",
                        returnShippingFeesAmount: {
                          "@type": "MonetaryAmount",
                          currency: "EUR",
                          value: 0,
                        },
                      },
                    },
                  },
                },
              },
            ],
          },
          {
            "@type": "ImageObject",
            "@id": "https://headlockr.io/#primaryimage",
            url: `https://headlockr.io/checkout/${price.id}`,
            contentUrl: productImage?.[0] || "https://www.headlockr.com/logo.png",
            caption: `Headlockr ${name} Plan thumbnail`,
            width: 438,
            height: 443,
          },
          {
            "@id": "https://headlockr.io/#website",
            "@type": "WebSite",
            url: "https://headlockr.io/",
            name: "Headlockr",
            description: "Secure your Strapi CMS with Headlockr",
            publisher: { "@id": "https://headlockr.io/#organization" },
            potentialAction: [
              {
                "@type": "BuyAction",
                target: {
                  "@type": "EntryPoint",
                  urlTemplate: `https://headlockr.io/checkout/${price.id}`,
                  actionPlatform: [
                    "https://schema.org/DesktopWebPlatform",
                    "https://schema.org/AndroidPlatform",
                    "https://schema.org/IOSPlatform",
                    "https://schema.org/MobileWebPlatform",
                    "https://schema.org/GenericWebPlatform",
                  ],
                },
              },
            ],
            inLanguage: "en-US",
            copyrightHolder: {
              "@id": "https://headlockr.io/#organization",
            },
          },
          {
            "@type": "OnlineBusiness",
            "@id": "https://headlockr.io/#organization",
            name: "Headlockr",
            legalName: "STRHUB B.V.",
            identifier: {
              // KVK
              "@type": "PropertyValue",
              propertyID: "KVK",
              value: "96493658",
            },
            description: "A company specializing in building secure software solutions for developers.",
            url: "https://headlockr.io",
            vatId: "NL867635307B01",
            telephone: "+31-20-1234567",
            email: "hi@headlockr.io",
            logo: "https://headlockr.io/logo.png",
            image: "https://headlockr.io/logo.png",
            foundingDate: "2024-01-01",
            foundingLocation: {
              "@type": "Place",
              name: "Goirle, Netherlands",
            },
            location: {
              "@type": "Place",
              name: "Goirle, Netherlands",
            },
            address: {
              "@type": "PostalAddress",
              streetAddress: "Parallelweg 14",
              addressLocality: "Goirle",
              addressRegion: "Noord-Brabant",
              postalCode: "5051HG",
              addressCountry: "NL",
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+31-20-1234567",
              contactType: "customer support",
              email: "support@headlockr.io",
              areaServed: "Worldwide",
            },
            sameAs: [
              "https://twitter.com/headlockr",
              "https://linkedin.com/company/headlockr",
              "https://github.com/headlockr",
            ],
            slogan: "Secure your Strapi CMS with Headlockr",
          },
        ],
      },
    },
    {
      name: "robots",
      content: "noindex, follow",
    },
  ];
};

const isAddressComplete = (address) => {
  return (
    address?.line1?.trim() !== "" &&
    address?.city?.trim() !== "" &&
    address?.country?.trim() !== "" &&
    address?.postal_code?.trim() !== ""
  );
};

function CheckoutForm() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { name, subtotal, metaData, ENV } = useLoaderData();

  const [paymentLoading, setPaymentLoading] = useState(false);
  const { priceId } = useParams();
  const { submit: submitVat, data: vatData } = useFetcher();
  const mainFormFetcher = useFetcher();
  const [cardComplete, setIsCardComplete] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [vatNumber, setVatNumber] = useState("");

  const [stripeValues, setStripeValues] = useState({
    name: null,
    address: null,
  });

  const [currentImage, setCurrentImage] = useState(CheckOutImage);
  const [imageIndex, setImageIndex] = useState(0);

  const [debouncedVatNumber] = useDebounceValue(vatNumber, 500); // Adjust delay as needed

  const handlePostalCodeChange = useCallback((event) => {
    const { name, address } = event.value;

    setStripeValues({
      name,
      address,
    });
  }, []);

  // Trigger the request when debounced value reaches 8+ characters
  // Validate the VAT Number
  useEffect(() => {
    if (debouncedVatNumber.length >= 8) {
      const formData = new FormData();
      formData.append("vatNumber", debouncedVatNumber);
      formData.append("country", stripeValues?.address?.country || stripeValues.country);

      submitVat(formData, {
        method: "post",
        action: "/checkout/validate-vat",
      });
    }
  }, [debouncedVatNumber, stripeValues, submitVat]);

  // Handler for monitoring card input completeness
  const handleCardChange = useCallback((event) => {
    setIsCardComplete(event.complete);
  }, []);

  // Confirm the payment after data is sent to strapi
  useEffect(() => {
    async function confirmPayment() {
      if (!stripe || !elements) {
        // Stripe.js hasn't yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        console.error("Stripe.js has not loaded yet");
        return;
      }

      const { type, clientSecret } = mainFormFetcher.data;

      if (!clientSecret || !type) {
        console.error("Unable to confirm payment");

        return;
      }

      const confirmIntent = type === "setup" ? stripe.confirmSetup : stripe.confirmPayment;

      setPaymentLoading(true);

      // Confirm the Intent using the details collected by the Payment Element
      const { error } = await confirmIntent({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${ENV.PUBLIC_URL}/success`,
        },
      });

      if (error) {
        navigate("/payment-failed", {
          replace: true,
          state: { reason: error?.message || "unknown" },
        });
        return;
      } else {
        // navigate("/account", { replace: false });
      }
    }

    mainFormFetcher.state === "idle" && mainFormFetcher.data && !mainFormFetcher.data?.error && confirmPayment();
  }, [mainFormFetcher, elements, stripe]);

  const plan = {
    name,
    features: [
      {
        text: "Protect for your Strapi admin panel & Content API",
        included: true,
      },
      {
        text: `Usage for ${metaData.project_limit} projects`,
        included: true,
      },
      {
        text: "7-day money back guarantee",
        included: true,
      },
      {
        text: "Product support",
        included: true,
      },
      {
        text: "Flexible API",
        included: true,
      },
    ],
  };

  // Submit the data to Strapi
  const handleSubmit = async () => {
    try {
      if (!window.grecaptcha) {
        alert("reCAPTCHA is not ready. Please refresh the page.");
        return;
      }

      const token = await executeRecaptcha("checkout");

      if (!token) {
        throw new Error("Failed to generate reCAPTCHA token.");
      }

      if (!stripe || !elements) {
        console.error("Stripe.js has not loaded yet");
        return;
      }

      const { error: submitError } = await elements.submit();

      if (submitError) {
        console.error("Error submitting card details", submitError);
        alert(`Payment error: ${submitError.message}`);
        return;
      }

      if (!stripeValues.name || !isAddressComplete(stripeValues.address) || !cardComplete) {
        alert("Please complete all required fields.");
        return;
      }

      const formData = new FormData(document.getElementById("checkout-form"));

      formData.append("recaptchaToken", token);
      formData.append("name", stripeValues.name);
      formData.append("address", JSON.stringify(stripeValues.address));
      formData.append("agreeTerms", agreeTerms);

      mainFormFetcher.submit(formData, {
        method: "post",
        action: "/checkout/subscribe",
      });
    } catch (error) {
      console.error("Error generating reCAPTCHA token:", error);
      alert("Error validating reCAPTCHA. Please try again.");
    }
  };

  useEffect(() => {
    const images = [CheckOutImage, CheckOutImage2];
    const interval = setInterval(() => {
      setImageIndex((prevIndex) => (prevIndex + 1) % images.length);
      setCurrentImage(images[(imageIndex + 1) % images.length]);
    }, 3000);

    return () => clearInterval(interval);
  }, [imageIndex]);

  return (
    <div className="container px-lg-2 pb-10 overflow-x-hidden">
      <div className="row">
        <div className="row align-items-start mb-1">
          <div className="col-lg-6 px-0 pt-2 pt-lg-0">
            <h1 className="mb-2 text-md-start">
              Start securing <br />
              <span className="position-relative d-inline-block">
                your
                <img src={UnderLine} alt="underline" className="position-absolute w-100 bottom-0 start-0" width={100} />
              </span>{" "}
              Strapi now
            </h1>
          </div>
          <div className="col-lg-6 px-0 pb-2 d-flex justify-content-start justify-content-md-end">
            <div className="d-flex align-items-center gap-1">
              <span className="text-white fw-bold">Built for </span>

              <div className="badge bg-white rounded">
                <img
                  src={StrapiLogoPill}
                  alt="Strapi logo"
                  style={{ width: 75 }}
                  className="img-fluid mx-auto d-block"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 bg-secondary p-lg-3 p-sm-2 rounded-4 mb-3 mb-md-0">
          {mainFormFetcher.data?.error && (
            <div className="alert bg-danger text-white">Something went wrong: {mainFormFetcher.data.error}</div>
          )}

          <mainFormFetcher.Form id="checkout-form">
            <div className="row mt-1">
              <div className="d-flex flex-column flex-md-row justify-content-between">
                <h3 className="h4 mb-2">Billing details</h3>
                <CheckoutLiveVisitors />
              </div>

              <div className="col-12 col-md-4 mb-0 mb-md-2">
                <EmailInput />
              </div>
              <div className="col-12 col-md-8">
                <div className="row">
                  <div className="col-12 col-md-6 mb-2 mb-md-0">
                    <label htmlFor="password" className="form-label text-white stripe-label">
                      Password
                    </label>
                    <input
                      className="form-control Input bg-darkblue py-1 text-white"
                      type="password"
                      autoComplete="new-password"
                      id="password"
                      name="password"
                      placeholder="Password"
                    />
                  </div>
                  <div className="col-12 col-md-6 mb-2 mb-md-0">
                    <label htmlFor="confirmPassword" className="form-label text-white stripe-label">
                      Confirm password
                    </label>
                    <input
                      className="form-control Input bg-darkblue py-1 text-white"
                      type="password"
                      autoComplete="new-password"
                      name="confirmPassword"
                      id="confirmPassword"
                      placeholder="Confirm Password"
                    />
                  </div>
                </div>
              </div>
              <input value={priceId} type="hidden" name="priceId" id="priceId" />
            </div>

            <AddressElement options={{ mode: "billing" }} onChange={handlePostalCodeChange} />

            <div className="my-2">
              {vatData && (
                <div className={`alert bg-${vatData.error ? "danger" : "success"} text-white`}>{vatData.message}</div>
              )}

              <label htmlFor="vatNumber" className="form-label text-white stripe-label">
                VAT Number (EU only)
              </label>

              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip className="z-10" id="encryption-info-tooltip">
                    VAT Reverse Charge – Customer is responsible for VAT under Article 44 of the VAT Directive
                    2006/112/EC.
                  </Tooltip>
                }
              >
                <img
                  style={{ cursor: "help" }}
                  className="ms-1"
                  src="https://img.icons8.com/?size=100&id=77&format=png&color=ffffff"
                  width={20}
                  alt="info"
                />
              </OverlayTrigger>

              <input
                disabled={!isAddressComplete(stripeValues.address)}
                type="text"
                name="vatNumber"
                id="vatNumber"
                placeholder="Please enter your EU VAT number including the country code"
                className="form-control Input bg-darkblue py-1  text-white"
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
              />
            </div>

            <div className="my-2">
              <PaymentElement onChange={handleCardChange} options={{}} />
            </div>
          </mainFormFetcher.Form>
        </div>

        <div className="col-12 p-0 col-md-6 ps-md-3">
          <div className="bg-secondary p-lg-2 p-1 rounded-4 position-relative mb-3">
            <h3 className="h4">Included with your purchase</h3>
            <ul className="p-0 text-center text-md-start">
              {plan.features.map((feature, featureIndex) => (
                <li
                  key={featureIndex}
                  className={`d-flex align-items-center  justify-content-md-start my-1 ${
                    feature.included ? "text-success" : "text-danger"
                  }`}
                >
                  {feature.included ? (
                    <img
                      src="https://img.icons8.com/?size=100&id=59850&format=png&color=00F6AC"
                      alt="checkmark"
                      style={{ width: 18, objectFit: "contain" }}
                    />
                  ) : (
                    <img
                      src="https://img.icons8.com/ios/50/000000/cancel.png"
                      alt="cancel"
                      style={{ width: 20, objectFit: "contain" }}
                    />
                  )}

                  <p className="ms-2 fs-6 mb-0">{feature.text}</p>
                </li>
              ))}
            </ul>
            <div className="bg-primary px-2 py-1 rounded-4 d-flex flex-column  align-items-md-start position-relative">
              <p className="fw-bold m-0"> System requirements: </p>
              <div className="d-flex gap-3 align-items-center my-1 flex-wrap">
                <div className="d-flex gap-1 align-items-center">
                  <img src={WebpackLogo} alt="Webpack logo" className="img-fluid" width={25} />
                  <p className="m-0 fs-6">Webpack</p>
                </div>
                <div className="d-flex gap-1 align-items-center">
                  <img
                    src="https://vite.dev/assets/vite-light.t8GCa_VF.svg"
                    alt="Vite logo"
                    className="img-fluid"
                    width={50}
                  />
                  <p className="m-0 fs-6">Vite</p>
                </div>
              </div>
              <div className="d-flex gap-1 align-items-center">
                <div className=" position-relative">
                  <img src={StrapiLogo} alt="Strapi logo" className="img-fluid" width={25} />
                  <span
                    className="bg-blue text-white fw-bold position-absolute"
                    style={{
                      fontSize: 8,
                      padding: "2px 5px ",
                      borderRadius: 5,
                      top: -8,
                      left: 10,
                    }}
                  >
                    {" "}
                    V4{" "}
                  </span>{" "}
                </div>

                <div className=" position-relative">
                  <img src={StrapiLogo} alt="Webpack logo" className="img-fluid" width={25} />
                  <span
                    className="bg-blue text-white fw-bold position-absolute"
                    style={{
                      fontSize: 8,
                      padding: "2px 5px ",
                      borderRadius: 5,
                      top: -8,
                      left: 10,
                    }}
                  >
                    {" "}
                    V5{" "}
                  </span>{" "}
                </div>
                <p className="m-0 fs-6"> Strapi V4/V5 support </p>
              </div>
            </div>

            <AnimatePresence>
              <motion.img
                key={currentImage}
                src={currentImage}
                alt="Checkout"
                className="img-fluid position-absolute end-0"
                style={{ width: 100, bottom: 20 }}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{
                  duration: 0.5,
                  ease: "easeInOut",
                }}
              />
            </AnimatePresence>
          </div>

          <div>
            <h4 className="fw-semibold mb-3">Order Summary</h4>

            <div className="d-flex justify-content-between">
              <p>Headlockr | Premium | Annual license</p>
              <p>€{subtotal.toFixed(2)}</p>
            </div>
            <div className="d-flex justify-content-between">
              <p className="fs-6 text-gray-neutral-800">7-day money-back guarantee included</p>
              <p className="fw-semibold text-gray-neutral-800">€{subtotal.toFixed(2)}</p>
            </div>

            {/* <div className="d-flex justify-content-between mb-2">
              <p>VAT ({checkoutData?.taxPercentage}%):</p>
              <p className="fw-semibold">
                {totalAmount !== null
                  ? `€${taxAmount.toFixed(2)}`
                  : "to be calculated"}
              </p>
            </div> */}

            <hr className="border" />

            <div className="d-flex justify-content-between align-items-center">
              <p className="fw-bold mb-2">Total:</p>
              <p className="text-tertiary fs-4 fw-bold mb-0">€ {subtotal.toFixed(2)}</p>
            </div>

            <div className="d-flex justify-content-end mb-3 text-gray-neutral-800">VAT calculated during payment</div>

            <div className="bg-secondary p-1 rounded-3 d-flex flex-column  align-items-md-start position-relative">
              <div className="d-flex justify-content-between w-100 align-items-center">
                {/* <img
                  src={productImage?.[0]}
                  alt="Logo"
                  className="img-fluid"
                  style={{
                    width: 100,
                    height: 50,
                    objectFit: "contain",
                  }}
                />
                <p className="fw-semibold">€{total.toFixed(2)}</p> */}
              </div>
              <ClientOnly>{() => <CountdownTimer />}</ClientOnly>
              <img
                src={SandClock}
                alt="Sand clock"
                className="img-fluid position-absolute clock"
                style={{
                  width: 100,
                  height: 100,
                  objectFit: "contain",
                  bottom: -25,
                  right: 0,
                }}
              />
            </div>

            {/* Newsletter Checkbox */}
            <div className="my-2">
              {/* Newsletter Switch (Now above the button) */}
              <div className="form-check form-switch my-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="agreeTerms"
                  name="agreeTerms"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                />
                <label className="form-check-label text-white" htmlFor="subscribeToNewsletter">
                  I agree to the{" "}
                  <a
                    className="text-tertiary"
                    href="https://headlockr.io/terms-and-conditions"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    terms and conditions.
                  </a>{" "}
                  Your subscription will be automatically renewed unless canceled
                  {/* <a
                    className="text-tertiary"
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    privacy policy
                  </a> */}
                  .
                </label>
              </div>
            </div>

            <button
              className="btn btn-tertiary w-100"
              onClick={handleSubmit}
              disabled={
                !cardComplete ||
                mainFormFetcher.state === "submitting" ||
                !stripeValues.name ||
                !isAddressComplete(stripeValues.address) ||
                !agreeTerms
              }
            >
              {mainFormFetcher.state === "submitting" || paymentLoading ? (
                <div className="spinner-border" role="status">
                  <span className="visually-hidden"> Processing...</span>
                </div>
              ) : (
                "Start protecting now"
              )}
            </button>
          </div>
          <div className="d-flex flex-column flex-md-row gap-3 gap-md-5 align-items-start align-items-md-center mt-3">
            <div className="d-flex gap-2">
              <img
                src="https://img.icons8.com/?size=100&id=93&format=png&color=ffffff"
                alt="lock"
                style={{ width: 20, objectFit: "contain" }}
              />
              <div>
                <p className="text-gray-100 fw-bold mb-0">Secure payment</p>
                <p className="text-gray-100 fs-6 mb-0">Connections are encrypted with SSL.</p>
              </div>
            </div>
            <div className="d-flex gap-2">
              <img
                src="https://img.icons8.com/?size=100&id=59850&format=png&color=ffffff"
                alt="lock"
                style={{ width: 20, objectFit: "contain" }}
              />
              <div>
                <p className="text-gray-100 fw-bold mb-0">Money back guarantee</p>
                <p className="text-gray-100 fs-6 mb-0">You have a 7-day money-back guarantee on your purchase.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-lg-10 mt-5">
        <div className="col-12 p-0">
          <Testimonial />
          <ToastNotification />
        </div>
      </div>
    </div>
  );
}

function EmailInput() {
  const fetcher = useFetcher();

  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);

  const handleBlur = () => {
    if (email.trim().length > 0) {
      setTouched(true);
      const formData = new FormData();
      formData.append("email", email);
      fetcher.submit(formData, {
        method: "post",
        action: "/lookup/customer",
      });
    }
  };

  const isLoading = fetcher.state === "submitting";
  const isAvailable = fetcher.data?.available;

  const handleInputChange = (e) => {
    setEmail(e.target.value);
    setTouched(false); // Reset validation state on input change
  };

  return (
    // <fetcher.Form method="post" action="/user/lookup">
    <div className="mb-3">
      <label htmlFor="email" className="form-label text-white stripe-label">
        Email
      </label>

      <input
        type="text"
        className={`form-control Input bg-darkblue py-1 text-white ${
          touched && !isLoading && !fetcher?.data?.error && (isAvailable ? "is-valid" : "is-invalid")
        }`}
        id="email"
        name="email"
        value={email}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder="Enter email"
        aria-label="Username"
        aria-describedby="username-adornment"
        autoComplete="email"
      />

      {touched && email.trim().length > 0 && !isLoading && !fetcher?.data?.error && (
        <div className={`form-text ${isAvailable ? "text-success" : "text-danger"}`}>
          {isAvailable ? "Email is available!" : "Email is already taken."}
        </div>
      )}

      {isLoading && (
        <span className="spinner-border spinner-border-sm text-info mt-1" role="status" aria-hidden="true">
          <span className="visually-hidden">Loading...</span>
        </span>
      )}

      {fetcher.data?.error && (
        <div className="form-text text-danger">Something went wrong. Please try again later.</div>
      )}
    </div>
    //</fetcher.Form>
  );
}

export default function CheckoutPage() {
  const { amount } = useLoaderData();

  return (
    <Elements
      stripe={stripePromise}
      options={{
        appearance: {
          theme: "night",
        },
        locale: "en",
        mode: "subscription",
        amount,
        currency: "eur",
        loader: "auto",
      }}
    >
      <CheckoutForm />
    </Elements>
  );
}
